import { useState } from 'react';
import { Reveal } from '@components/motion';
import { useToast } from '@components/Toast';
import { api, ApiError } from '@/lib/api';
import { ABOUT, headingParts } from '@/data/about';

const INPUT_CLASS =
    'w-full rounded-lg border border-[rgba(245,245,245,0.46)] bg-transparent px-3 py-3 text-[16px] text-white placeholder:text-[#6c6c6c] focus:border-white/70 focus:outline-none';

export default function Feedback() {
    const heading = headingParts(ABOUT.feedback.heading);
    const toast = useToast();
    const [form, setForm] = useState({ email: '', name: '', message: '' });
    const [sending, setSending] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();

        const missing: string[] = [];
        if (!form.email.trim()) missing.push('an email');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) missing.push('a valid email');
        if (!form.message.trim()) missing.push('your feedback');

        if (missing.length) {
            toast.error('We still need ' + missing.join(' and '), 'Fill those in and send again.');
            return;
        }

        setSending(true);
        try {
            await api.post('/submissions/feedback', {
                name: form.name.trim(),
                email: form.email.trim(),
                message: form.message.trim(),
            });
            toast.success('Thanks — your feedback is in', 'We read every one, and reply when a reply is useful.');
            setForm({ email: '', name: '', message: '' });
        } catch (error) {
            toast.error(
                'That did not send',
                error instanceof ApiError && error.fields
                    ? Object.values(error.fields)[0]
                    : 'Something went wrong on our side. Try again in a moment.',
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section className="relative overflow-hidden bg-[#01050a] text-white">
            {/* Decorative faded panel (right) */}
            <Reveal
                as="div"
                once
                variant="fade"
                duration={1200}
                className="pointer-events-none absolute right-0 top-0 hidden h-full w-[37%] overflow-hidden rounded-l-[30px] bg-[#010205] lg:block"
            >
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        backgroundImage:
                            'radial-gradient(120% 120% at 80% 20%, rgba(255,102,0,0.18) 0%, rgba(1,2,5,0) 60%)',
                    }}
                />
            </Reveal>

            <div className="relative mx-auto max-w-[1920px] px-5 py-16 lg:px-[140px] lg:py-[120px]">
                <Reveal
                    as="h2"
                    once
                    variant="fade-up"
                    className="text-[44px] font-extrabold leading-[1.05] lg:text-[96px] lg:leading-[103px]"
                >
                    <span className="font-normal text-[#6c6c6c]">{heading.lead}</span>
                    <span>{heading.bold}</span>
                </Reveal>
                <Reveal
                    as="p"
                    once
                    variant="fade-up"
                    delay={120}
                    className="mt-5 text-[22px] font-semibold text-[#bcbcbc] lg:text-[36px]"
                >
                    {ABOUT.feedback.subheading}
                </Reveal>

                <form className="mt-8 max-w-[1233px]" onSubmit={submit} noValidate>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[540px_651px]">
                        <div className="flex flex-col gap-4">
                            <Reveal once variant="fade-up" delay={200}>
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    className={INPUT_CLASS}
                                    value={form.email}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                />
                            </Reveal>
                            <Reveal once variant="fade-up" delay={280}>
                                <input
                                    type="text"
                                    placeholder="Enter full name"
                                    className={INPUT_CLASS}
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                />
                            </Reveal>
                        </div>
                        <Reveal once variant="fade-up" delay={360} className="lg:h-full">
                            <textarea
                                placeholder="Enter your feedback"
                                className={`${INPUT_CLASS} h-[112px] resize-none lg:h-full`}
                                value={form.message}
                                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                            />
                        </Reveal>
                    </div>
                    <Reveal once variant="fade-up" delay={460}>
                        <button
                            type="submit"
                            disabled={sending}
                            className="mt-6 flex h-[48px] w-full items-center justify-center rounded-[10px] bg-[#f60] px-5 text-[16px] font-bold text-white transition-transform duration-300 hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-60 lg:w-[216px]"
                        >
                            {sending ? 'Sending…' : ABOUT.feedback.submitLabel}
                        </button>
                    </Reveal>
                </form>
            </div>
        </section>
    );
}
